/*
 * The BetterFX page's "Make it yours" section (sections/betterfx-style.liquid).
 * The Mode and Accent swatches restyle the real search bar floating over
 * the timeline, and the two switches show or hide the 3rd-party tag and
 * the legacy effect, as the plugin's Search settings do. Click a result
 * (or focus the bar and use the arrow keys) to move the selection.
 *
 * While it's on screen and nobody has touched it, it tries a few looks by
 * itself, now and then moving the selection a row like the arrow keys do.
 * The visitor's first click or key press stops that for good. Off screen
 * or in a hidden tab it waits. With reduced motion it stays a still
 * Dark / Blue bar that still answers clicks.
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');

  // The auto tour: a new look, then a step of the selection, and so on.
  const TOUR = [
    { mode: 'dark', accent: 'blue', sel: 0, hold: 1500 },
    { sel: 1, hold: 1300 },
    { mode: 'light', accent: 'orange', hold: 1700 },
    { sel: 2, hold: 1400 },
    { mode: 'glass', accent: 'purple', hold: 1900 },
    { sel: 3, hold: 1300 },
    { mode: 'dark', accent: 'green', hold: 1700 },
    { sel: 2, hold: 1300 },
    { mode: 'light', accent: 'pink', hold: 1700 },
    { sel: 1, hold: 1300 },
    { mode: 'glass', accent: 'yellow', hold: 1900 },
    { sel: 0, hold: 1300 },
    { mode: 'dark', accent: 'red', hold: 1700 },
  ];
  const BAR_W = 360;

  const init = (root) => {
    if (root.dataset.bsReady) return;
    root.dataset.bsReady = 'true';

    const stage = root.querySelector('[data-bs-stage]');
    const bar = root.querySelector('[data-bs-bar]');
    if (!stage || !bar) return;
    const rows = [...root.querySelectorAll('[data-bs-row]')];
    const modeButtons = [...root.querySelectorAll('[data-bs-mode]')];
    const accentButtons = [...root.querySelectorAll('[data-bs-accent]')];
    const switches = [...root.querySelectorAll('[data-bs-switch]')];
    const tag = root.querySelector('[data-bs-tag]');
    const legacyRow = root.querySelector('[data-bs-legacy]');

    const state = { mode: 'dark', accent: 'blue', sel: 0 };
    let fadeTimer = 0;

    const visibleRows = () => rows.filter((row) => !row.hidden);

    const paintSelection = () => {
      const list = visibleRows();
      state.sel = Math.max(0, Math.min(state.sel, Math.min(list.length, 5) - 1));
      rows.forEach((row) => {
        const on = row === list[state.sel];
        row.classList.toggle('is-selected', on);
        row.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    };

    const setLook = (mode, accent) => {
      if (mode === state.mode && accent === state.accent) return;
      state.mode = mode;
      state.accent = accent;
      // a soft fade for a new look; arrow-key moves stay instant, as in the app
      bar.classList.add('is-fading');
      window.clearTimeout(fadeTimer);
      fadeTimer = window.setTimeout(() => bar.classList.remove('is-fading'), 620);
      root.dataset.mode = mode;
      root.dataset.accent = accent;
      modeButtons.forEach((b) => b.setAttribute('aria-checked', b.dataset.bsMode === mode ? 'true' : 'false'));
      accentButtons.forEach((b) => b.setAttribute('aria-checked', b.dataset.bsAccent === accent ? 'true' : 'false'));
    };

    const setSel = (index) => {
      state.sel = index;
      paintSelection();
    };

    // ---- Fit the 360px bar to the stage ----
    const fit = () => {
      const width = stage.clientWidth;
      if (!width) return;
      const room = width - (width < 520 ? 20 : 120);
      const scale = Math.max(0.6, Math.min(1.2, room / BAR_W));
      root.style.setProperty('--bs-scale', scale.toFixed(3));
    };
    fit();
    if ('ResizeObserver' in window) new ResizeObserver(fit).observe(stage);
    else window.addEventListener('resize', fit);

    // ---- The auto tour ----
    let step = 1;
    let timer = 0;
    let onScreen = false;
    let handedOver = still.matches;

    const tick = () => {
      const s = TOUR[step];
      if (s.mode) setLook(s.mode, s.accent);
      if (typeof s.sel === 'number') setSel(s.sel);
      step = (step + 1) % TOUR.length;
      timer = window.setTimeout(tick, s.hold);
    };

    const pause = () => {
      window.clearTimeout(timer);
      timer = 0;
    };

    const resume = () => {
      if (handedOver || timer || !onScreen || document.hidden) return;
      timer = window.setTimeout(tick, 1400);
    };

    const handOver = () => {
      if (handedOver) return;
      handedOver = true;
      pause();
    };

    // ---- The visitor's turn ----
    root.addEventListener('pointerdown', handOver, { passive: true });
    root.addEventListener('keydown', handOver);

    modeButtons.forEach((b) =>
      b.addEventListener('click', () => {
        handOver();
        setLook(b.dataset.bsMode, state.accent);
      })
    );
    accentButtons.forEach((b) =>
      b.addEventListener('click', () => {
        handOver();
        setLook(state.mode, b.dataset.bsAccent);
      })
    );

    // Arrow keys move through a radio group, as native radios do.
    [modeButtons, accentButtons].forEach((group) =>
      group.forEach((b, i) =>
        b.addEventListener('keydown', (event) => {
          const delta = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
          if (!delta) return;
          event.preventDefault();
          const next = group[(i + delta + group.length) % group.length];
          next.focus();
          next.click();
        })
      )
    );

    switches.forEach((sw) =>
      sw.addEventListener('click', () => {
        handOver();
        const on = sw.getAttribute('aria-checked') !== 'true';
        sw.setAttribute('aria-checked', on ? 'true' : 'false');
        if (sw.dataset.bsSwitch === 'third' && tag) tag.hidden = !on;
        if (sw.dataset.bsSwitch === 'legacy' && legacyRow) {
          const selected = visibleRows()[state.sel];
          legacyRow.hidden = !on;
          // keep the same effect selected when the rows shift
          const list = visibleRows();
          const kept = list.indexOf(selected);
          state.sel = kept >= 0 ? kept : Math.max(0, state.sel - 1);
        }
        paintSelection();
      })
    );

    rows.forEach((row) =>
      row.addEventListener('click', () => {
        handOver();
        const index = visibleRows().indexOf(row);
        if (index >= 0 && index < 5) setSel(index);
      })
    );

    bar.addEventListener('keydown', (event) => {
      const delta = { ArrowDown: 1, ArrowUp: -1 }[event.key];
      if (!delta) return;
      event.preventDefault();
      setSel(Math.max(0, Math.min(4, state.sel + delta)));
    });

    paintSelection();

    if (handedOver || !('IntersectionObserver' in window)) return;

    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          onScreen = entry.isIntersecting;
          if (onScreen) resume();
          else pause();
        });
      },
      { threshold: 0.35 }
    ).observe(stage);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) pause();
      else resume();
    });
  };

  const boot = () => document.querySelectorAll('[data-bfx-style]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
